import { CommonModel } from '@telialt/models/src/CommonModel';
import { datadogLogger } from '@telialt/utils/src/datadog/datadogLogger';
import { DDComponent } from '@telialt/utils/src/datadog/setup';

import { apiGetSuggestionsQuery } from '../../api';
import { SEARCH_AUTOCOMPLETE_SUGGESTIONS_COUNT } from '../../constants';
import {
    SearchQueryKeysMap,
    SearchSuggestionItem,
    SearchSuggestionItemWithQueryKey,
    SearchSuggestionsParams,
} from '../../model';
import { getQueryKeyPrefixBySegment } from '../../utils/segment';

type Params = {
    searchText: string;
    segment: CommonModel.Segment;
    queryKeys: SearchQueryKeysMap;
    signal?: AbortSignal;
};

type SuggestionsWithQueryKey = {
    suggestions: SearchSuggestionItem[];
    queryKey: string;
};

const mapSuggestionWithQueryKey = (
    { suggestion }: SearchSuggestionItem,
    queryKey: string,
): SearchSuggestionItemWithQueryKey => {
    return { suggestion, queryKey };
};

const pickSuggestionsToDisplay = (
    products: SuggestionsWithQueryKey,
    services: SuggestionsWithQueryKey,
    help: SuggestionsWithQueryKey,
    content: SuggestionsWithQueryKey,
): SearchSuggestionItemWithQueryKey[] => {
    const mappedProducts = products.suggestions.map((suggestion) =>
        mapSuggestionWithQueryKey(suggestion, products.queryKey),
    );
    const mappedServices = services.suggestions.map((suggestion) =>
        mapSuggestionWithQueryKey(suggestion, services.queryKey),
    );
    const mappedHelp = help.suggestions.map((suggestion) => mapSuggestionWithQueryKey(suggestion, help.queryKey));
    const mappedContent = content.suggestions.map((suggestion) =>
        mapSuggestionWithQueryKey(suggestion, content.queryKey),
    );

    return [...mappedProducts, ...mappedServices, ...mappedHelp, ...mappedContent]
        .filter(
            (item, index, allItems) =>
                allItems.findIndex((findItem) => findItem.suggestion === item.suggestion) === index,
        )
        .slice(0, SEARCH_AUTOCOMPLETE_SUGGESTIONS_COUNT);
};

export const loadAutoCompleteSuggestions = async ({ searchText, segment, queryKeys, signal }: Params) => {
    const queryKeyPrefix = getQueryKeyPrefixBySegment(segment);

    const searchParams: SearchSuggestionsParams = {
        searchText,
        limit: SEARCH_AUTOCOMPLETE_SUGGESTIONS_COUNT,
    };

    const productsPromise =
        segment === 'b2c' || segment === 'b2b'
            ? apiGetSuggestionsQuery(queryKeys[`${queryKeyPrefix}-products-suggestions`], searchParams, signal)
            : undefined;
    const servicesPromise = apiGetSuggestionsQuery(
        queryKeys[`${queryKeyPrefix}-services-suggestions`],
        searchParams,
        signal,
    );
    const helpPromise =
        segment === 'b2c' || segment === 'b2b'
            ? apiGetSuggestionsQuery(queryKeys[`${queryKeyPrefix}-help-suggestions`], searchParams, signal)
            : undefined;
    const contentPromise = apiGetSuggestionsQuery(
        queryKeys[`${queryKeyPrefix}-content-suggestions`],
        searchParams,
        signal,
    );

    try {
        const [productsResponse, servicesResponse, helpResponse, contentResponse] = await Promise.all([
            productsPromise,
            servicesPromise,
            helpPromise,
            contentPromise,
        ]);

        const productSuggestions = productsResponse?.data || [];
        const servicesSuggestions = servicesResponse?.data || [];
        const helpSuggestions = helpResponse?.data || [];
        const contentSuggestions = contentResponse?.data || [];

        const suggestions = pickSuggestionsToDisplay(
            { suggestions: productSuggestions, queryKey: queryKeys[`${queryKeyPrefix}-products-suggestions`] },
            { suggestions: servicesSuggestions, queryKey: queryKeys[`${queryKeyPrefix}-services-suggestions`] },
            { suggestions: helpSuggestions, queryKey: queryKeys[`${queryKeyPrefix}-help-suggestions`] },
            { suggestions: contentSuggestions, queryKey: queryKeys[`${queryKeyPrefix}-content-suggestions`] },
        );

        return { suggestions };
    } catch (error) {
        datadogLogger().warn(error, { component: DDComponent.Search });

        return { error };
    }
};
